import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useUserContext } from "../../utils/UserContext";
import { tincPermis } from "../../utils/Permisos";
import { mapToPresentable, mapejos } from "../../utils/Mapejos";
import { API_URL } from "../../utils/config";

const PAGE_SIZES = [10, 25, 50];
const TIPUS_ORDERS = [
  ["Empresa", "Autonom", "Particular"],
  ["Autonom", "Particular", "Empresa"],
  ["Particular", "Empresa", "Autonom"],
];

function idClient(c) {
  return c ? Number(c.idClients) : null;
}

function nomComplet(client) {
  return [
    [client.Cognom1, client.Cognom2].filter((x) => x && String(x).trim()).join(" "),
    client.Nom,
  ]
    .filter((x) => x && String(x).trim())
    .join(", ");
}

function telefonPrincipal(client) {
  return client.TelefonFixe || client.TelefonMobil || "";
}

function SeleccionarClient({
  onClientSelect,
  onConsultar,
  onModificar,
  onEsborrar,
  onContactes,
  onTreballs,
  onPressupostos,
  selectedClient,
  filterText,
  setFilterText,
  setClients,
  reloadToken,
}) {
  const { empresaSeleccionada, rolUsuari } = useUserContext();
  const [clients, setClientsState] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [menuObert, setMenuObert] = useState(null);
  const [menuPos, setMenuPos] = useState(null);
  const [isNarrow, setIsNarrow] = useState(false);
  const menuRef = useRef(null);

  const pucConsultar = tincPermis(rolUsuari, "CONSULTAR");
  const pucModificar = tincPermis(rolUsuari, "MODIFICAR");
  const pucEsborrar = tincPermis(rolUsuari, "ESBORRAR");

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const apply = () => setIsNarrow(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    const fetchClients = async () => {
      if (!empresaSeleccionada?.IdEmpresa) return;
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/Clients.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idEmpresa: empresaSeleccionada.IdEmpresa }),
        });
        const result = await response.json();
        setClientsState(result.success ? result.clients || [] : []);
      } catch (err) {
        console.error("Error carregant clients:", err);
        setClientsState([]);
      } finally {
        setLoading(false);
      }
    };
    fetchClients();
  }, [empresaSeleccionada, reloadToken]);

  const handleSort = (key) => {
    if (key === "TipusEntitat") {
      const currentIndex = TIPUS_ORDERS.findIndex(
        (order) => JSON.stringify(order) === JSON.stringify(sortConfig.tipusOrder)
      );
      const nextTipusOrder = TIPUS_ORDERS[(currentIndex + 1) % TIPUS_ORDERS.length];
      setSortConfig({ key, direction: "asc", tipusOrder: nextTipusOrder });
      return;
    }
    const direction = sortConfig.key === key && sortConfig.direction === "asc" ? "desc" : "asc";
    setSortConfig({ key, direction, tipusOrder: sortConfig.tipusOrder });
  };

  useEffect(() => {
    const t = (filterText || "").toLowerCase();
    const filtered = (clients || []).filter((client) => {
      const camp = [
        client.Nom,
        client.Cognom1,
        client.Cognom2,
        client.NomVia,
        client.Poblacio,
        client.TelefonFixe,
        client.TelefonMobil,
        client.TelefonMobil2,
        client.Whatsapp,
        client.Email1,
        client.Email2,
        client.CIF_DNI,
        client.NomComercial,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return !t || camp.includes(t);
    });

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue;
        let bValue;
        if (sortConfig.key === "TipusEntitat") {
          const tipusOrder = sortConfig.tipusOrder || TIPUS_ORDERS[0];
          aValue = tipusOrder.indexOf(a.TipusEntitat);
          bValue = tipusOrder.indexOf(b.TipusEntitat);
        } else if (sortConfig.key === "Nom") {
          aValue = nomComplet(a).toLowerCase();
          bValue = nomComplet(b).toLowerCase();
        } else if (sortConfig.key === "Telefon") {
          aValue = telefonPrincipal(a).toString();
          bValue = telefonPrincipal(b).toString();
        } else {
          aValue = (a[sortConfig.key] || "").toString().toLowerCase();
          bValue = (b[sortConfig.key] || "").toString().toLowerCase();
        }
        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    setFilteredClients(filtered);
    setClients?.(filtered);
    setPage(1);
  }, [filterText, clients, sortConfig, setClients]);

  useEffect(() => {
    if (!selectedClient) return;
    const stillVisible = filteredClients.some((c) => idClient(c) === idClient(selectedClient));
    if (!stillVisible) onClientSelect?.(null);
  }, [filteredClients, selectedClient, onClientSelect]);

  useEffect(() => {
    const tancar = (e) => {
      if (e.target.closest(".dv2-menu") || e.target.closest(".dv2-menuBtn")) return;
      setMenuObert(null);
    };
    document.addEventListener("mousedown", tancar);
    return () => document.removeEventListener("mousedown", tancar);
  }, []);

  const obrirMenu = (e, client) => {
    e.stopPropagation();
    const id = idClient(client);
    if (menuObert === id) {
      setMenuObert(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const ample = 220;
    const alt = 280;
    const marge = 8;
    let top = rect.bottom + 4;
    if (top + alt > window.innerHeight - marge) {
      top = Math.max(marge, rect.top - alt - 4);
    }
    let left = rect.right - ample;
    if (left < marge) left = marge;
    if (left + ample > window.innerWidth - marge) {
      left = window.innerWidth - ample - marge;
    }
    setMenuPos({ top, left });
    setMenuObert(id);
  };

  const total = filteredClients.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const paginaActual = Math.min(page, totalPages);
  const inici = (paginaActual - 1) * pageSize;
  const paginaItems = filteredClients.slice(inici, inici + pageSize);
  const clientMenu = menuObert ? filteredClients.find((c) => idClient(c) === menuObert) : null;

  const paginesVisibles = () => {
    const pages = [];
    const max = totalPages;
    const actual = paginaActual;
    const afegir = (n) => {
      if (!pages.includes(n)) pages.push(n);
    };
    afegir(1);
    for (let i = actual - 1; i <= actual + 1; i += 1) {
      if (i > 1 && i < max) afegir(i);
    }
    if (max > 1) afegir(max);
    return pages.sort((a, b) => a - b);
  };

  const renderAccions = (client) => (
    <div className="dv2-table-actions">
      <button
        type="button"
        className={`dv2-menuBtn ${menuObert === idClient(client) ? "is-open" : ""}`}
        aria-label="Accions"
        onClick={(e) => obrirMenu(e, client)}
      >
        …
      </button>
    </div>
  );

  const th = (key, label) => (
    <th className={sortConfig.key === key ? "is-sorted" : ""} onClick={() => handleSort(key)}>
      {label} {sortConfig.key === key ? (sortConfig.direction === "asc" ? "↑" : "↓") : ""}
    </th>
  );

  const tancarI = (fn) => {
    setMenuObert(null);
    fn?.();
  };

  return (
    <div className="dv2-listBody">
      <div className="dv2-filterBar">
        <div className="dv2-filter dv2-filter--grow">
          <span className="dv2-filter-label">Cerca</span>
          <input
            type="text"
            className="dv2-input dv2-input-search"
            placeholder="Cerca per nom, cognoms, adreça, telèfon o e-mail…"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <p className="dv2-empty">Carregant clients…</p>
      ) : filteredClients.length === 0 ? (
        <p className="dv2-empty">No s'ha trobat cap client.</p>
      ) : (
        <>
          {isNarrow ? (
            <div className="dv2-recordList" style={{ display: "flex" }}>
              {paginaItems.map((client) => (
                <article
                  key={idClient(client)}
                  className="dv2-record"
                  onClick={() => onClientSelect?.(client)}
                  onDoubleClick={() => pucConsultar && onConsultar?.(client)}
                >
                  <div>
                    <p className="dv2-record-title">{nomComplet(client) || "—"}</p>
                    <p className="dv2-record-meta">
                      {[
                        mapToPresentable(client.TipusEntitat, mapejos.tipusEntitat),
                        client.Poblacio,
                        telefonPrincipal(client),
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  </div>
                  {renderAccions(client)}
                </article>
              ))}
            </div>
          ) : (
            <div className="dv2-tableWrap">
              <table className="dv2-table">
                <thead>
                  <tr>
                    {th("TipusEntitat", "Organització")}
                    {th("CIF_DNI", "CIF/NIF")}
                    {th("Nom", "Nom")}
                    {th("NomComercial", "Nom comercial")}
                    {th("Telefon", "Telèfon")}
                    {th("Email1", "E-mail")}
                    {th("Poblacio", "Població")}
                    <th className="dv2-table-actions">Accions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginaItems.map((client) => {
                    const tipus = mapToPresentable(client.TipusEntitat, mapejos.tipusEntitat);
                    return (
                      <tr
                        key={idClient(client)}
                        className={idClient(selectedClient) === idClient(client) ? "is-selected" : ""}
                        onClick={() => onClientSelect?.(client)}
                        onDoubleClick={() => pucConsultar && onConsultar?.(client)}
                      >
                        <td>{tipus || "—"}</td>
                        <td>
                          {tipus === "Empresa" ? "CIF" : "NIF"}: {client.CIF_DNI || "—"}
                        </td>
                        <td>{nomComplet(client) || "—"}</td>
                        <td>{client.NomComercial || "—"}</td>
                        <td>{telefonPrincipal(client) || "—"}</td>
                        <td>{client.Email1 || "—"}</td>
                        <td>{client.Poblacio || "—"}</td>
                        <td className="dv2-table-actions">{renderAccions(client)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="dv2-pagination">
            <span>
              Mostrant {inici + 1} a {Math.min(inici + pageSize, total)} de {total} resultats
            </span>
            <div className="dv2-pagination-pages">
              <button type="button" className="dv2-pageBtn dv2-pageBtn--nav" disabled={paginaActual <= 1} onClick={() => setPage(1)}>
                «
              </button>
              <button
                type="button"
                className="dv2-pageBtn dv2-pageBtn--nav"
                disabled={paginaActual <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                ‹
              </button>
              {paginesVisibles().map((n, idx, arr) => (
                <React.Fragment key={n}>
                  {idx > 0 && n - arr[idx - 1] > 1 && <span>…</span>}
                  <button
                    type="button"
                    className={`dv2-pageBtn ${n === paginaActual ? "is-active" : ""}`}
                    onClick={() => setPage(n)}
                  >
                    {n}
                  </button>
                </React.Fragment>
              ))}
              <button
                type="button"
                className="dv2-pageBtn dv2-pageBtn--nav"
                disabled={paginaActual >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                ›
              </button>
              <button
                type="button"
                className="dv2-pageBtn dv2-pageBtn--nav"
                disabled={paginaActual >= totalPages}
                onClick={() => setPage(totalPages)}
              >
                »
              </button>
            </div>
            <label className="dv2-filter">
              <span className="dv2-filter-label">Files per pàgina</span>
              <select
                className="dv2-select"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                {PAGE_SIZES.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </>
      )}

      {clientMenu &&
        menuPos &&
        createPortal(
          <div className="dv2-menu dv2-menu--fixed" ref={menuRef} style={{ top: menuPos.top, left: menuPos.left }}>
            {pucConsultar && (
              <button type="button" className="dv2-menu-item" onClick={() => tancarI(() => onConsultar?.(clientMenu))}>
                Consultar
              </button>
            )}
            {pucModificar && (
              <button type="button" className="dv2-menu-item" onClick={() => tancarI(() => onModificar?.(clientMenu))}>
                Modificar
              </button>
            )}
            {pucModificar && (
              <button type="button" className="dv2-menu-item" onClick={() => tancarI(() => onContactes?.(clientMenu))}>
                Contactes
              </button>
            )}
            {pucConsultar && (
              <button type="button" className="dv2-menu-item" onClick={() => tancarI(() => onTreballs?.(clientMenu))}>
                Treballs
              </button>
            )}
            {pucConsultar && (
              <button type="button" className="dv2-menu-item" onClick={() => tancarI(() => onPressupostos?.(clientMenu))}>
                Pressupostos
              </button>
            )}
            {pucEsborrar && (
              <button type="button" className="dv2-menu-item is-danger" onClick={() => tancarI(() => onEsborrar?.(clientMenu))}>
                Esborrar
              </button>
            )}
          </div>,
          document.body
        )}
    </div>
  );
}

export default SeleccionarClient;
